import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Loader2, CheckCircle2, Clock, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ProcessingShipmentRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incoming');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/processing/shipment-requests');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmShipment = async (transferId) => {
    setIsProcessing(true);
    try {
      await apiClient.post('/processing/confirm-shipment', {
        consignment_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      shipment_requested: { color: 'bg-orange-100 text-orange-800', label: 'Requested' },
      shipment_confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed' }
    };
    const cfg = config[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  // Filter requests based on tab
  const incomingRequests = requests.filter(r => r.processing_status === 'shipment_requested' || r.processing_status === 'shipment_confirmed');
  const outgoingRequests = []; // Will be populated when processing sends goods out

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/processing')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Shipment Requests</h1>
              <p className="text-sm text-gray-400">Processing Section</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="incoming" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Incoming ({incomingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="outgoing" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Outgoing ({outgoingRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="incoming">
            {incomingRequests.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No incoming shipment requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {incomingRequests.map((request) => (
                  <Card key={request.transfer_id} className="bg-white shadow-md">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-lg">{request.transfer_id}</CardTitle>
                          {getStatusBadge(request.processing_status)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">From Section</p>
                          <p className="font-medium">{request.from_section}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Bundles</p>
                          <p className="font-medium">{request.bundle_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Processing Unit</p>
                          <p className="font-medium text-blue-600">{request.processing_unit_name || 'Not Assigned'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Requested At</p>
                          <p className="font-medium text-sm">
                            {request.shipment_requested_at ? new Date(request.shipment_requested_at).toLocaleString() : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Design Summary */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {Object.entries(request.design_wise_summary || {}).map(([design, data]) => (
                          <Badge key={design} variant="outline" className="bg-gray-50">
                            {design}: {data.count}
                          </Badge>
                        ))}
                      </div>

                      {/* Status Indicator */}
                      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${request.processing_status === 'shipment_confirmed' ? 'bg-green-500' : 'bg-orange-500'}`} />
                          <span className="text-sm">
                            {request.processing_status === 'shipment_confirmed' 
                              ? 'Shipment confirmed - Awaiting arrival'
                              : 'Waiting for source to confirm shipment'}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      {request.processing_status === 'shipment_requested' && (
                        <div className="text-sm text-gray-500 italic">
                          Waiting for {request.from_section} to confirm the shipment...
                        </div>
                      )}
                      
                      {request.processing_status === 'shipment_confirmed' && (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <span className="text-green-700 font-medium">Shipment confirmed! Go to Inward Consignment to mark as received.</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="outgoing">
            <Card className="bg-white">
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No outgoing shipment requests</p>
                <p className="text-sm text-gray-400 mt-2">
                  Outgoing requests will appear here when you mark goods as ready
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ProcessingShipmentRequests;
